import {
  CompanyEntity,
  getRequiredObjectId,
  siteAssignmentStrategies,
  toSiteAssignmentStrategy
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {ActionIcon, Button, Center, Divider, Grid, Group, Text} from "@mantine/core";
import {useCallback, useMemo} from "react";
import {
  SiteAssignmentStrategySelect
} from "../../../select/entity/SiteAssignmentStrategySelect/SiteAssignmentStrategySelect";
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import {IconGripVertical, IconTrash} from "@tabler/icons-react";
import {useDisclosure} from "@mantine/hooks";
import {TruckCompanySelect, TruckCompanySelectModal} from "../../../select/entity/CompanySelect/TruckCompanySelect";
import {
  CompanySiteAssignmentFormFieldValues,
  CompanySiteAssignmentFormProvider,
  CompanySiteAssignmentFormValues,
  SiteAssignmentCompanyAssocFormValues,
  useCompanySiteAssignmentForm,
  useCompanySiteAssignmentFormContext
} from "./form-context";

interface CompanySiteAssignmentFormProps {
  defaultValues?: Partial<CompanySiteAssignmentFormValues>;
  onSubmit?: (values: CompanySiteAssignmentFormValues) => void;
  submitting?: boolean;
  options?: CompanySiteAssignmentFormOptions
}

export interface CompanySiteAssignmentFormOptions {
  regionId?: string
}

export const getDefaultCompanySiteAssignmentFormValues = (defaultValues: Partial<CompanySiteAssignmentFormValues> | undefined) => {
  return {
    strategy: defaultValues?.strategy?.code ?? siteAssignmentStrategies.ranked.code,
    normalCompanies: defaultValues?.assocs?.sort((a, b) => a.normalRank - b.normalRank).map((assoc) => ({
      id: getRequiredObjectId(assoc.company),
    })) ?? [],
    serviceCompanies: defaultValues?.assocs?.sort((a, b) => a.serviceRank - b.serviceRank).map((assoc) => ({
      id: getRequiredObjectId(assoc.company),
    })) ?? [],
  }
}

export const CompanySiteAssignmentFormFields = (props: { options?: CompanySiteAssignmentFormOptions }) => {
  const { options } = props;

  const {t} = useTranslation(["translation", "common"]);
  const [opened, handlers] = useDisclosure(false);

  const form = useCompanySiteAssignmentFormContext()

  const selectedCompanyIds = useMemo(() => {
    return form.values.normalCompanies.map((s, _) => {
      return s.id
    })
  }, [form.values.normalCompanies])

  const onCompanySelect = useCallback((companies: CompanyEntity[] | undefined) => {
    companies?.forEach((s) => {
      const exists = selectedCompanyIds.findIndex(id => s.id === id) >= 0
      if (!exists) {
        form.insertListItem('normalCompanies', {id: s.id})
        form.insertListItem("serviceCompanies", {id: s.id})
      }
    })
    handlers.close()
  }, [form, handlers, selectedCompanyIds])

  const onCompanyRemove = useCallback((idToRemove: string) => {
    let indexes: number[] = []

    form.values.normalCompanies.forEach((s, index) => {
      if (s.id === idToRemove) {
        indexes.push(index)
      }
    })

    indexes.forEach((index) => {
      form.removeListItem('normalCompanies', index)
    })

    indexes = []

    form.values.serviceCompanies.forEach((s, index) => {
      if (s.id === idToRemove) {
        indexes.push(index)
      }
    })

    indexes.forEach((index) => {
      form.removeListItem('serviceCompanies', index)
    })

  }, [form])

  const normalCompanyFields = form.values.normalCompanies.map((s, index) => (
    <Draggable key={index} index={index} draggableId={index.toString()}>
      {(provided) => (
        <Group ref={provided.innerRef} mt="xs" {...provided.draggableProps}>
          <Center {...provided.dragHandleProps}>
            <IconGripVertical size="1.2rem"/>
          </Center>
          <TruckCompanySelect
            {...form.getInputProps(`normalCompanies.${index}.id`)}
            selector={{
              regionId: options?.regionId
            }}
            disabled={true}/>
          <ActionIcon>
            <IconTrash size="1.2rem" onClick={() => onCompanyRemove(s.id)}/>
          </ActionIcon>
        </Group>
      )}
    </Draggable>
  ));

  const serviceCompanyFields = form.values.serviceCompanies.map((s, index) => (
    <Draggable key={index} index={index} draggableId={index.toString()}>
      {(provided) => (
        <Group ref={provided.innerRef} mt="xs" {...provided.draggableProps}>
          <Center {...provided.dragHandleProps}>
            <IconGripVertical size="1.2rem"/>
          </Center>
          <TruckCompanySelect
            {...form.getInputProps(`serviceCompanies.${index}.id`)}
            selector={{
              regionId: options?.regionId
            }}
            disabled={true}/>
          <ActionIcon>
            <IconTrash size="1.2rem" onClick={() => onCompanyRemove(s.id)}/>
          </ActionIcon>
        </Group>
      )}
    </Draggable>
  ));

  return (
    <>
      <SiteAssignmentStrategySelect
        label={t("common.entity.siteassignment.form.field.strategy", "Strategy", {ns: "common"})}
        placeholder={t<string>("common.entity.siteassignment.form.field.strategy.placeholder", "Strategy", {ns: "common"})}
        clearable
        withAsterisk
        required
        {...form.getInputProps("strategy")} />
      <Divider my="sm" variant="dotted"/>

      <Grid>
        <Grid.Col span={5}>
          <Text fw={700}>Normal Work Rank</Text>
          <DragDropContext
            onDragEnd={({destination, source}) =>
              form.reorderListItem('normalCompanies', {from: source.index, to: destination!.index})
            }
          >
            <Droppable droppableId="dnd-list" direction="vertical">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {normalCompanyFields}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Grid.Col>

        <Grid.Col span={1}>
          <Divider my="sm" variant="dotted" orientation="vertical"/>
        </Grid.Col>

        <Grid.Col span={5}>
          <Text fw={700}>Service Work Ranks</Text>
          <DragDropContext
            onDragEnd={({destination, source}) =>
              form.reorderListItem('serviceCompanies', {from: source.index, to: destination!.index})
            }
          >
            <Droppable droppableId="dnd-list" direction="vertical">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {serviceCompanyFields}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </Grid.Col>
      </Grid>

      <Divider my="sm" variant="dotted"/>

      <Group position="center" mt="md">
        <Button onClick={handlers.open}>
          Add Company
        </Button>
      </Group>

      <TruckCompanySelectModal
        select={{
          withinPortal: true,
          selector: {
            regionId: options?.regionId,
          }
        }}
        size="lg"
        opened={opened}
        onClose={handlers.close}
        onSubmit={onCompanySelect}/>
    </>
  );
}

export const getCompanySiteAssignmentFormValues = (values: CompanySiteAssignmentFormFieldValues): CompanySiteAssignmentFormValues => {
  const assocs: SiteAssignmentCompanyAssocFormValues[] = []
  values.normalCompanies.forEach((ns, i) => {
    assocs.push({
      company: ns.id,
      normalRank: i,
      serviceRank: values.serviceCompanies.findIndex((ss) => ss.id === ns.id)
    })
  })

  return {
    strategy: toSiteAssignmentStrategy(values.strategy),
    assocs: assocs,
  }
}

export const CompanySiteAssignmentForm = (props: CompanySiteAssignmentFormProps) => {
  const {
    defaultValues,
    options,
    onSubmit,
    submitting = false
  } = props;

  const {t} = useTranslation(["translation", "common"]);

  const form = useCompanySiteAssignmentForm({
    initialValues: getDefaultCompanySiteAssignmentFormValues(defaultValues),
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      const formValues = getCompanySiteAssignmentFormValues(values)
      onSubmit(formValues);
    }
  }, [form, onSubmit]);

  return (
    <CompanySiteAssignmentFormProvider form={form}>
      <form onSubmit={form.onSubmit(onFormSubmit)}>
        <CompanySiteAssignmentFormFields options={options}/>
        <Divider my="sm" variant="dotted"/>
        <Button type="submit" loading={submitting} fullWidth mt="xl">
          {t("common.action.submit", {ns: "common"})}
        </Button>
      </form>
    </CompanySiteAssignmentFormProvider>
  );
};
